import { useCallback, useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { useIsNewEntry, usePostApi } from "src/hooks";
import { useDispatch, useSelector } from "src/redux/hooks";
import {
  setEntryLockData,
  setReleaseEntryLock,
} from "src/redux/slices/entrySlice";

/**
 * V2 Entry Lock Management
 */
export const useV2EntryLock = () => {
  const { post } = usePostApi();
  const { idType } = useParams();
  const current = useSelector(state => state.entrySlice.current);
  const editing = useSelector(state => state.entrySlice.editing);
  const activeAutosave = useSelector(state => state.entrySlice.activeAutosave);
  const locked = useSelector(state => state.entrySlice.locked);
  const you = useSelector(state => state.entrySlice.you);
  const openedSaveModal = useSelector(
    state => state.entrySlice.openedSaveModal,
  );
  const releaseEntryLock = useSelector(
    state => state.entrySlice.releaseEntryLock,
  );

  const isNewEntry = useIsNewEntry();
  const dispatch = useDispatch();

  const id = useSelector(state => state.generatorSlice.latestEntry.id);
  const idObject = useMemo(
    () => ({ [String(idType)]: isNewEntry ? "new" : id }),
    [idType, isNewEntry, id],
  );
  const lastExistingIdObjectRef = useRef<Record<string, string> | null>(null);

  const keepAliveIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lockCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const keepAliveDelayRef = useRef<NodeJS.Timeout | null>(null);
  const lastCheckLockTimeRef = useRef<number>(0);
  const prevOpenedSaveModalRef = useRef(openedSaveModal);

  useEffect(() => {
    if (
      !isNewEntry &&
      idObject[String(idType)] &&
      idObject[String(idType)] !== "new"
    ) {
      lastExistingIdObjectRef.current = { ...idObject };
    }
  }, [idObject, idType, isNewEntry]);

  // Get lock from server
  const handleGetLock = useCallback(() => {
    if (!window.navigator.onLine) return;

    if (isNewEntry) {
      // New entry - auto-grant lock locally
      dispatch(
        setEntryLockData({
          locked: true,
          user: null,
          you: true,
          changed: 0,
          activeAutosave: true,
        }),
      );
    } else {
      // Existing entry - request lock from server
      void post({
        request: "get-entry-lock",
        body: { params: { ...idObject, current } },
        onSuccess: data => {
          dispatch(
            setEntryLockData({
              ...data,
              activeAutosave: data.locked && data.you,
              lockedByOther: data.locked && !data.you,
            }),
          );
        },
      });
    }
  }, [idObject, current, dispatch, isNewEntry, post]);

  // Check lock status (when not editing)
  const handleCheckLock = useCallback(() => {
    if (!window.navigator.onLine) return;
    if (isNewEntry) return;

    void post({
      request: "check-entry-lock",
      body: { params: { ...idObject, current } },
      onSuccess: data =>
        dispatch(
          setEntryLockData({
            ...data,
            activeAutosave: false,
          }),
        ),
    });
  }, [idObject, current, isNewEntry, post, dispatch]);

  // Keep lock alive (only while editing)
  const handleKeepAlive = useCallback(() => {
    if (!window.navigator.onLine) return;
    if (isNewEntry) return; // New entries don't need keep-alive

    void post({
      request: "keep-alive-entry-lock",
      body: { params: { ...idObject } },
      onError: () => {
        // Lock lost, try to reclaim it
        void handleGetLock();
      },
    });
  }, [idObject, isNewEntry, post, handleGetLock]);

  // Release lock
  const handleReleaseLock = useCallback(async () => {
    if (!window.navigator.onLine) return;

    const releaseParams = !isNewEntry
      ? idObject
      : lastExistingIdObjectRef.current;

    if (releaseParams) {
      await post({
        request: "release-entry-lock",
        body: { params: { ...releaseParams } },
      });
    }

    // Clear local lock state
    dispatch(
      setEntryLockData({
        locked: false,
        you: false,
        user: null,
        activeAutosave: false,
      }),
    );
  }, [idObject, isNewEntry, dispatch, post]);

  // Cleanup intervals - removed, now handled inline in effect
  const clearIntervals = useCallback(() => {
    if (keepAliveIntervalRef.current) {
      clearInterval(keepAliveIntervalRef.current);
      keepAliveIntervalRef.current = null;
    }
    if (lockCheckIntervalRef.current) {
      clearInterval(lockCheckIntervalRef.current);
      lockCheckIntervalRef.current = null;
    }
    if (keepAliveDelayRef.current) {
      clearTimeout(keepAliveDelayRef.current);
      keepAliveDelayRef.current = null;
    }
  }, []);

  useEffect(() => {
    const wasOpen = prevOpenedSaveModalRef.current;
    const wasCanceled = wasOpen && !openedSaveModal;

    // If save modal was canceled (closed without successful save signal),
    // release lock immediately so it doesn't remain held while idle.
    if (wasCanceled && !editing && locked && you && releaseEntryLock === 0) {
      clearIntervals();
      void handleReleaseLock();
    }

    prevOpenedSaveModalRef.current = openedSaveModal;
  }, [
    clearIntervals,
    editing,
    handleReleaseLock,
    locked,
    openedSaveModal,
    releaseEntryLock,
    you,
  ]);

  // Main lock management effect
  useEffect(() => {
    let checkLockInterval: NodeJS.Timeout;

    const canCheckLock = Boolean(current) && Boolean(id) && !isNewEntry;
    const isEditingOrSaving = editing || openedSaveModal;

    // When NOT editing: check lock status every 3 seconds
    if (!isEditingOrSaving && canCheckLock && (!locked || (locked && !you))) {
      // Throttle the immediate check to prevent double-firing on initial load
      const now = Date.now();
      if (now - lastCheckLockTimeRef.current > 2500) {
        lastCheckLockTimeRef.current = now;
        void handleCheckLock();
      }

      checkLockInterval = setInterval(() => {
        lastCheckLockTimeRef.current = Date.now();
        void handleCheckLock();
      }, 3000);
    }

    // When editing: get lock and keep it alive
    if (isEditingOrSaving) {
      // Get lock when we don't have one yet
      if (!activeAutosave && !locked) {
        void handleGetLock();
      }

      // Keep lock alive only when we have it
      if (activeAutosave && locked && you) {
        if (!keepAliveDelayRef.current && !keepAliveIntervalRef.current) {
          // Delay first keep-alive by 5s after lock acquisition
          keepAliveDelayRef.current = setTimeout(() => {
            void handleKeepAlive();

            // Set up recurring keep-alive every 5s after first delay
            keepAliveIntervalRef.current = setInterval(() => {
              void handleKeepAlive();
            }, 5000);
          }, 5000);
        }
      }
    } else {
      // If stopped editing, clear keep-alive intervals immediately
      if (keepAliveDelayRef.current) {
        clearTimeout(keepAliveDelayRef.current);
        keepAliveDelayRef.current = null;
      }
      if (keepAliveIntervalRef.current) {
        clearInterval(keepAliveIntervalRef.current);
        keepAliveIntervalRef.current = null;
      }
    }

    return () => {
      if (checkLockInterval) clearInterval(checkLockInterval);
      // Let keep-alive stay active until editing stops or component unmounts
    };
  }, [
    editing,
    openedSaveModal,
    activeAutosave,
    locked,
    you,
    current,
    id,
    isNewEntry,
    handleGetLock,
    handleCheckLock,
    handleKeepAlive,
  ]);

  // Handle lock release signal from autosave
  useEffect(() => {
    if (releaseEntryLock !== 0) {
      clearIntervals();
      void handleReleaseLock();
      dispatch(setReleaseEntryLock(0));
    }
  }, [releaseEntryLock, clearIntervals, handleReleaseLock, dispatch]);

  // Release lock on component unmount
  useEffect(() => {
    return () => {
      clearIntervals();
      if (!window.location.href.includes("logbook")) {
        void handleReleaseLock();
      }
    };
  }, [clearIntervals, handleReleaseLock]);
};
